import { Herd } from 'app/agriculture/queries/herds/types/Herd';
import { useHerds } from 'app/agriculture/queries/herds/useHerds';
import { getPositionNearCenter } from 'app/agriculture/hooks/useMapRender/mapRenderUtils';
import { useSnapshots } from 'app/agriculture/queries/snapshots/useSnapshots';
import { HerdSnapshot } from 'app/agriculture/queries/herds/types/HerdSnapshot';
import { useEntities } from 'app/agriculture/queries/entities/useEntities';
import { useMemo } from 'react';
import { Position } from 'app/agriculture/queries/types/geometry/Position';

/**
 * Gets herd data ready for presentation the map. Turns herds and snapshots into concrete locations
 */
export function useHerdsOnMap() {
    const herds = useHerds();
    const snapshots = useSnapshots({
        snapshotType: 'Herd',
        shelfLife: null,
    });
    const paddocks = useEntities('Paddock');

    const herdsByPaddockId = useMemo(() => {
        const map = new Map<string, Herd[]>();
        for (const herd of herds.values()) {
            const { paddockId } = snapshots.get(herd.id) ?? {};
            if (paddockId != null) {
                map.set(paddockId, [...(map.get(paddockId) ?? []), herd]);
            }
        }
        return map;
    }, [herds, snapshots]);

    return useMemo(() => {
        const map = new Map<
            string,
            Herd & { location: Position | null; snapshot: HerdSnapshot | null }
        >();
        for (const [paddockId, herds] of herdsByPaddockId) {
            const paddock = paddocks.get(paddockId);
            if (paddock) {
                herds.forEach((herd, index) => {
                    const snapshot = snapshots.get(herd.id);
                    if (!snapshot) return;
                    map.set(herd.id, {
                        ...herd,
                        location: getPositionNearCenter(herds.length, index, paddock.geometry),
                        snapshot,
                    });
                });
            }
        }
        return map;
    }, [herdsByPaddockId, paddocks, snapshots]);
}
